import React from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Popover, PopoverContent, PopoverTrigger, Modal, ModalContent, ModalBody } from "@heroui/react";
import { useNavigate, useParams } from "react-router-dom";

import { store, useAppSelector } from '@/app/store';
import { AppsHolder, AppDescriptor, copyAppDescriptor, copyAppsHolder, getNewAppDescriptor } from '@/features/apps/appsSlice';

import Button from '@/components/Common/Button';
import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import Input from '@/components/Common/Input';
import MenuItem from '@/components/Common/MenuItem';
import { NumberText } from '@/components/Common/Field';
import { DeleteModal } from '@/components/Common/Modals';

import { CatHeader } from '@/pages/Projects/Project';
import { RoleContext } from '@/app/api/common';
import { usePageSubscription } from '@/app/websocket/hooks';

const Apps = () => {
  const { t } = useTranslation();

  usePageSubscription([{ type: 'app' }]);

  const apps = useAppSelector((state) => state.apps);
  const navigate = useNavigate();
  const { app_id } = useParams();

  const [cols, setCols] = React.useState([] as any[]);
  const [rows, setRows] = React.useState([] as AppDescriptor[]);
  const [deleteNum, setDeleteNum] = React.useState(-1);
  const role = React.useContext(RoleContext);

  const appNum = React.useMemo(() => {
    if (!app_id) return -1;
    if (app_id === '__new__') return -2;
    const idx = apps?.apps?.findIndex((a: any) => a.id === app_id);
    return idx !== undefined && idx >= 0 ? idx : -1;
  }, [app_id, apps.apps]);
  
  React.useEffect(() => {
    store.getApps();
  }, []);
  
  React.useEffect(() => {
    setCols([
    {
        field:			"platform",
        headerName:		t("apps:app:platform:caption"),
        flex:			0,
        minWidth:		100,
        maxWidth:		150,
    },
    {
        field:			"id",
        headerName:		t("apps:app:id:caption"),
        flex:			1,
        minWidth:		150,
        maxWidth:		250,
    },
    {
        field:			"name",
        headerName:		t("apps:app:name:caption"),
        flex:			0,
        minWidth:		150,
        maxWidth:		200,
    },
    {
        field:			"logo",
        headerName:		t("apps:app:logo:caption"),
        flex:			2,
        minWidth:		250,
    },
    {
        field:			"install_id",
        headerName:		t("apps:app:install_id:caption"),
        flex:			1,
        minWidth:		150,
        maxWidth:		250,
    },
    ...(role.admin || role?.privilages?.includes("ChangeApps") || role?.privilages?.includes("DeleteApps") ? [{
        field:			"options",
        headerName:		t("apps:app:options:caption"),
        flex:			0,
        filter:			false,
        minWidth:		90,
        maxWidth:		90,
        cellRenderer:	OptionsCell,
    }] : []),
    ]);
    
    const rws: AppDescriptor[] = [];
    for (let i = 0; i < apps.apps.length; i++) {
      let app = copyAppDescriptor(apps.apps[i]);

      let appAny = app as any;

      appAny.requestDelete = () => setDeleteNum(i);
      appAny.requestEdit = () => navigate('/settings/apps/' + encodeURIComponent(apps.apps[i].id));
      appAny.__index__ = i;

      rws.push(app);
    }
    
    setRows(rws);
  }, [apps.apps, t]);
  
  const updateIsOpen = (val: boolean) => {
    if (!val)
      navigate('/settings/apps');
  };
  
  const processDelete = React.useCallback(async (obj: Object, answer: string) => {
    setDeleteNum(-1);
    
    if (answer !== "yes") {
      return;
    }
    
    if (deleteNum < -1)
      return;
    
    const napps = copyAppsHolder(apps);
    if (	!napps ||
        	napps.apps.length <= deleteNum
       )
      return;
    
    napps.apps.splice(deleteNum, 1);
    
    const mess = await store.setApps(napps);
    console.log("Apps/processDelete/deleteNum=" + deleteNum + "/mess=", mess);
  }, [apps.apps, deleteNum]);
  
  const updateApp = React.useCallback(async (n: number, app: AppDescriptor) => {
    const napps = copyAppsHolder(apps);
    if (	!app ||
    		n == -1 ||
    		!napps ||
        	napps.apps.length <= n
       )
      return;
    
    const napp = copyAppDescriptor(app);
    if (n == -2) {
      napps.apps.push(napp);
    } else {
      napps.apps[n] = napp;
    }
    
    const mess = await store.setApps(napps);
    console.log("Apps/updateApp/n=" + n + "/mess=", mess);
  }, [apps.apps]);
  
  const handleCellClick = React.useCallback((evt: any) => {
    if (evt.colDef.field == "options")
      return;
    if(!role.admin && !role?.privilages?.includes("ViewApps"))
      return
    navigate('/settings/apps/' + encodeURIComponent(evt.data.id));
  }, [navigate, role.admin, role?.privilages]);
  
  const getAppDesc = React.useCallback((n: number): AppDescriptor | null => {
    if (n == -2)
      return getNewAppDescriptor();
    
    if (	n < 0 ||
    		n >= rows.length
       )
      return null;
    
    return rows[n];
  }, [rows]);
  
  return (
  <React.Fragment>
    <FlexRow direction="column"
	         align="start"
	         className={[
               'bg-white',
               'px-[32px] pt-[32px] sticky top-[64px] z-10',
             ]}>
      <div className="text-[32px]/[48px]">
        { t('apps:caption') }
      </div>
      <div>
        <NumberText>
          { rows.length }
        </NumberText>
        { ' ' +
          t('apps:apps')
        }
      </div>
      <Divider className="my-[8px]" />
      { (role.admin || role?.privilages?.includes("ChangeApps")) &&
        <Button caption={t('apps:new_app')}
                icon="add"
                className="uppercase mt-[20px]"
                onClick={() => navigate('/settings/apps/__new__')} />
      }
    </FlexRow> 
    <FlexRow direction="column"
             align="start"
             className={[
               "py-[16px]",
               "px-[32px]"
             ]}>
    
      <Grid rowData={rows}
            columnDefs={cols}
            filter={true}
            globalFilter={true}
            showPages={true}
            maxHeight="calc(100vh - 380px)"
            onCellClicked={handleCellClick}
            pageSize={10} />
    </FlexRow>
  {appNum != -1 &&
    <Modal backdrop="opaque" 
           defaultOpen={true} 
           isDismissable={false}
           onOpenChange={updateIsOpen}
           classNames={{
             backdrop: "bg-[#0000007f]",
             wrapper: "justify-end"
           }}
           hideCloseButton={true}
           radius="none"
           placement="top">
      <ModalContent className="h-full !mx-0 !my-0 w-full max-w-[620px]">
        <ModalBody className="w-full">
          <FlexRow direction="column"
                   flex={0}>
            <App app={getAppDesc(appNum)}
                 index={appNum}
                 setApp={(app: AppDescriptor) => updateApp(appNum, app)}
                 onClose={() => navigate('/settings/apps')} />
          </FlexRow>
        </ModalBody>
      </ModalContent>
    </Modal>
  }
  {deleteNum > -1 && deleteNum < rows.length &&
	<DeleteModal title={t('apps:delete_app:caption')}
	             message={t('apps:delete_app:message_n', {n: rows[deleteNum].name})}
	             onResult={processDelete}>
      <>
      </>
    </DeleteModal>
  }
  </React.Fragment>
  );
};

const OptionsCell = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [open, setOpen] = React.useState(false);
  const role = React.useContext(RoleContext);
  const handleEdit = React.useCallback(() => {
    setOpen(false);
    props.data.requestEdit();
  }, [setOpen, props.data]);
  
  const handleDelete = React.useCallback(async () => {
    setOpen(false);
    props.data.requestDelete();
  }, [setOpen, props.data]);
  
  return (
  <Popover key={1}
           isOpen={open}
           placement="bottom-end"
           classNames={{
             content: "rounded-none",
           }}
           onOpenChange={setOpen}>
    <PopoverTrigger>
      <div className="h-full w-full text-right">
        <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
          more_vert
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[205px] py-[8px] gap-[8px]">
        {(role.admin || role?.privilages?.includes("ChangeApps")) &&
          <MenuItem icon="edit_note"
                    label={t('apps:app:edit_app')}
                    onClick={handleEdit} />
        }
        {(role.admin || role?.privilages?.includes("DeleteApps")) &&
          <MenuItem icon="delete"
                    color="!text-[--Warning]"
                    label={t('apps:delete_app:caption')}
                    onClick={handleDelete} />
        }
      </FlexRow>
    </PopoverContent>
  </Popover>
  );
};

export interface AppProps {
	index:				number;
	app:				AppDescriptor | null;
	setApp:				(app: AppDescriptor) => any;
	onClose:			() => any;
	//requestDelete:		() => any;
}

const App = (props: AppProps) => {
  const { t } = useTranslation();
  
  const isNew = props.index == -2;
  
  const [app, setApp] = React.useState(null as AppDescriptor | null);
  const [changes, setChanges] = React.useState(false);
  const [triedSave, setTriedSave] = React.useState(false);
  
  const platformOpts = React.useMemo<FieldInfo[]>(() => [
    { value:			"android",
      desc:			t("apps:app:platform:android"),
    },
    { value:			"philips",
      desc:			t("apps:app:platform:philips"),
    },
    { value:			"tizen",
      desc:			t("apps:app:platform:tizen_legacy"),
    },
    { value:			"webos",
      desc:			t("apps:app:platform:webos"),
    },
  ], [t]);
  
  React.useEffect(() => {
    setApp(props.app);
  }, [props.app]);
  
  const platformInvalid = React.useMemo(() => {
    return !app || app.platform.trim() =='';
  }, [app]);
  
  const idInvalid = React.useMemo(() => {
    return !app || app.id.trim() =='';
  }, [app]);
  
  const nameInvalid = React.useMemo(() => {
    return !app || app.name.trim() =='';
  }, [app]);
  
  const logoInvalid = React.useMemo(() => {
    return !app || app.logo.trim() =='';
  }, [app]);
  
  const updatePlatform = React.useCallback((val: string) => {
    if (!app)
      return;
    
    let napp = copyAppDescriptor(app);
    napp.platform = val;
    
    setApp(napp);
    setChanges(true);
  }, [app, setApp]);
  
  const updateId = React.useCallback((val: string) => {
    if (!app)
      return;
    
    let napp = copyAppDescriptor(app);
    napp.id = val;
    
    setApp(napp);
    setChanges(true);
  }, [app, setApp]);
  
  const updateInstallId = React.useCallback((val: string) => {
    if (!app)
      return;
    
    let napp = copyAppDescriptor(app);
    napp.install_id = val;
    
    setApp(napp);
    setChanges(true);
  }, [app, setApp]);
  
  const updateName = React.useCallback((val: string) => {
    if (!app)
      return;
    
    let napp = copyAppDescriptor(app);
    napp.name = val;
    
    setApp(napp);
    setChanges(true);
  }, [app, setApp]);
  
  const updateLogo = React.useCallback((val: string) => {
    if (!app)
      return;
    
    let napp = copyAppDescriptor(app);
    napp.logo = val;
    
    setApp(napp);
    setChanges(true);
  }, [app, setApp]);
  
  const handleSave = React.useCallback(() => {
    if (!app || platformInvalid || idInvalid || nameInvalid || logoInvalid) {
      setTriedSave(true);
      return;
    }
    
    setTriedSave(false);
    props.setApp(app);
    setChanges(false);
    props.onClose();
  }, [app, props.setApp]);
  
  if (!app)
    return;
  
  return (
  <React.Fragment>
    <FlexRow direction="column"
             align="start"
             className="px-[32px]">
      <FlexRow direction="column"
		       align="start"
		       className="py-0 gap-[16px] max-w-[620px]">
	    <CatHeader caption={isNew ? 'apps:app::new_app' : 'apps:app:edit_app'} />
	    
		<Dropdown label={t('apps:app:platform:caption') + '*'}
              value={app ? app.platform : ""}
              options={platformOpts}
              isInvalid={triedSave && platformInvalid}
              errorMessage={t('apps:app:platform:invalid')}
              setValue={updatePlatform}>
		  <>
		  </>
		</Dropdown>
		<Input label={t('apps:app:id:caption') + '*'}
		       isInvalid={triedSave && idInvalid}
		       errorMessage={t('apps:app:id:invalid')}
		       value={app ? app.id : ''}
		       onValueChange={updateId} />
		<Input label={t('apps:app:name:caption') + '*'}
		       isInvalid={triedSave && nameInvalid}
		       errorMessage={t('apps:app:name:invalid')}
		       value={app ? app.name : ''}
		       onValueChange={updateName} />
		<Input label={t('apps:app:logo:caption') + '*'}
		       isInvalid={triedSave && logoInvalid}
		       errorMessage={t('apps:app:logo:invalid')}
		       value={app ? app.logo : ''}
		       onValueChange={updateLogo} />
		<Input label={t('apps:app:install_id:caption') + '*'}
		       value={app ? app.install_id : ''}
		       onValueChange={updateInstallId} />
	  </FlexRow>
    </FlexRow>
    <FlexRow className="absolute bottom-0 p-[32px] bg-white z-10 justify-end gap-[16px]">
      <Button className="uppercase"
              caption={t('apps:app:cancel')}
              onClick={() => props.onClose()}
              layout="white" />
      <Button caption={t('apps:app:save')}
              icon="save"
              isDisabled={!changes || (triedSave && (platformInvalid || idInvalid || nameInvalid || logoInvalid))}
              onClick={() => handleSave()} />
    </FlexRow>
  </React.Fragment>
  );
};

export default Apps;

